'use client';

import { ApprovalStatusCard } from '@/components/features/dashboard/approval-status-card';
import { AveragePriceMetrics } from '@/components/features/dashboard/average-price-metrics';
import { BookmarkedStations } from '@/components/features/dashboard/bookmarked-stations';
import { DashboardHeader } from '@/components/features/dashboard/dashboard-header';
import {
  DomesticOilPriceChart,
  InternationalOilPriceChart,
} from '@/components/features/dashboard/oil-price-chart';
import { PieCharts } from '@/components/features/dashboard/pie-charts';
import { SalesChart } from '@/components/features/dashboard/sales-chart';
import { StoreTreeMap } from '@/components/features/dashboard/store-treemap';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useState } from 'react';

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0].replace(/-/g, '');
};

const getDefaultDateRange = () => {
  const today = new Date();
  const startDate = new Date(today);
  return {
    startDate,
    endDate: today,
  };
};

export function DashboardPage() {
  useDocumentTitle('Console');

  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>(getDefaultDateRange());

  const [pieData, setPieData] = useState<
    | {
        oilData: Array<{ name: string; amt: number; qty: number; cnt: number }>;
        saleTypeData: Array<{
          name: string;
          amt: number;
          qty: number;
          cnt: number;
        }>;
      }
    | undefined
  >();

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      const newStartDate = date;
      const today = new Date();

      // 시작일이 오늘 이후면 오늘로 제한
      if (newStartDate > today) {
        newStartDate.setTime(today.getTime());
      }

      // 종료일이 시작일보다 이전이면 시작일로 맞춤
      let newEndDate = dateRange.endDate;
      if (newEndDate < newStartDate) {
        newEndDate = newStartDate;
      }

      // 한 달 이상 차이나면 시작일 + 30일로 제한
      const maxEndDate = new Date(newStartDate);
      maxEndDate.setDate(maxEndDate.getDate() + 30);
      if (newEndDate > maxEndDate) {
        newEndDate = maxEndDate;
      }

      setDateRange({
        startDate: newStartDate,
        endDate: newEndDate,
      });
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      const newEndDate = date;
      const today = new Date();

      // 종료일이 오늘 이후면 오늘로 제한
      if (newEndDate > today) {
        newEndDate.setTime(today.getTime());
      }

      // 종료일이 시작일보다 이전이면 시작일로 맞춤
      if (newEndDate < dateRange.startDate) {
        newEndDate.setTime(dateRange.startDate.getTime());
      }

      // 한 달 이상 차이나면 종료일 - 30일로 제한
      const maxStartDate = new Date(newEndDate);
      maxStartDate.setDate(maxStartDate.getDate() - 30);
      let newStartDate = dateRange.startDate;
      if (newStartDate < maxStartDate) {
        newStartDate = maxStartDate;
      }

      setDateRange({
        startDate: newStartDate,
        endDate: newEndDate,
      });
    }
  };

  const dateProps = {
    startDate: formatDate(dateRange.startDate),
    endDate: formatDate(dateRange.endDate),
  };

  return (
    <div className='p-3 space-y-3'>
      <DashboardHeader
        dateRange={dateRange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
      />

      {/* 전국 평균 유가 정보 */}
      <AveragePriceMetrics />

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* 왼쪽 8/12 영역 */}
        <div className='lg:col-span-2 space-y-6'>
          {/* 주유소별 판매 통계 트리맵 */}
          <StoreTreeMap {...dateProps} />

          {/* 판매 차트 */}
          <SalesChart {...dateProps} onPieDataChange={setPieData} />

          {/* 국내/국제 유가 차트 */}
          <DomesticOilPriceChart />
          <InternationalOilPriceChart />
        </div>

        {/* 오른쪽 4/12 영역 */}
        <div className='lg:col-span-1 space-y-6'>
          {/* 전자결재 문서현황 (기본 접힘) */}
          <ApprovalStatusCard />

          {/* 파이 차트들 */}
          <PieCharts {...dateProps} pieData={pieData} />

          {/* 북마크된 주유소 */}
          <BookmarkedStations />
        </div>
      </div>
    </div>
  );
}
